import { useViewport } from "@xyflow/react";
import { type CSSProperties, useCallback, useContext, useEffect, useState } from "react";
import cn from "classnames";
import * as HoverCard from ".";

type Style = Pick<CSSProperties, "left" | "bottom" | "transform" | "opacity">;

const initialHoverCardBodyStyle: Style = { opacity: 0 };

const useStyle = (triggerEl?: HTMLElement): Style => {
  const [style, setStyle] = useState<Style>(initialHoverCardBodyStyle);
  const { zoom } = useViewport();

  useEffect(() => {
    if (triggerEl) {
      const rect = triggerEl.getBoundingClientRect();
      const left = rect.x;
      const bottom = window.innerHeight - rect.y;
      const transform = `scale(${zoom * 100}%)`;

      setStyle({
        left,
        bottom,
        transform,
        opacity: 1,
      });
    } else {
      setStyle(initialHoverCardBodyStyle);
    }
  }, [triggerEl, zoom]);

  return style;
};

export const ariaPrefix = {
  dialog: "hoverCard",
  label: "hoverCardLabel",
  desc: "hoverCardDesc",
};

export default function HoverCardBody() {
  const {
    state: { open, id, content, triggerEl },
    dispatch,
  } = useContext(HoverCard.Context);
  const style = useStyle(triggerEl);

  const handleEnter = useCallback(() => {
    dispatch({ type: HoverCard.ActionType.ClearTimeout });
    dispatch({ type: HoverCard.ActionType.Open });
  }, [dispatch]);

  const handleLeave = useCallback(() => {
    dispatch({ type: HoverCard.ActionType.Close });
  }, [dispatch]);

  if (!open) {
    return null;
  }

  const dialogId = ariaPrefix.dialog + id;
  const ariaLabelledbyId = ariaPrefix.label + id;
  const ariaDescribedbyId = ariaPrefix.desc + id;

  return (
    <div
      aria-labelledby={ariaLabelledbyId}
      aria-describedby={ariaDescribedbyId}
      className={cn(
        "absolute",
        "w-80",
        "-ml-px", // tweak spacing to cover Triggers with box-shadow borders
        "pb-1", // create a small gap between Trigger and this HoverCard
        "origin-bottom-left",
        "transition-opacity",
        "z-20", // TODO: create and use a z-index alias in Tailwind config
      )}
      id={dialogId}
      onBlur={handleLeave}
      onFocus={handleEnter}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      role="dialog"
      style={style}
    >
      <div className="text-charcoal-300 shadow-depth-800 overflow-hidden rounded-lg bg-white">
        {content}
      </div>
    </div>
  );
}
